import React from 'react'
import { connect } from 'react-redux'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { createStructuredSelector } from 'reselect'
import { GridApi, IHeaderParams } from 'ag-grid-community'
import styled from '@emotion/styled'
import { intersection, isEmpty, noop } from 'lodash-es'
import { DefaultCellContentWrapper } from './DefaultCellRenderer'
import { RowDataGetter } from 'view/shared/datagrid'
import { GlobalState } from 'store'
import { TypedCellRendererParams } from '../gridUtils'
import { withRenderOnEvents } from '../withRenderOnEvents'
import { getVisibleLeafChildren } from '../getVisibleLeafNodes'
export interface OptionalTestProps {
  dataTestId?: string
}

export const createMakeTestId = (testId: string | undefined) => (componentTestId: string) =>
  testId ? `${testId}-${componentTestId}` : componentTestId
export interface ActionsButtonProps<T = unknown> extends OptionalTestProps {
  gridApi?: GridApi
  items: T[]
  isCounterpartyNegotiation: boolean
  isGroupRow: boolean
  context?: Record<string, any>
}

const HeaderCellContentWrapper = styled(DefaultCellContentWrapper)`
  padding: 0;
`
interface GroupedActionRendererProps<T = unknown> extends IHeaderParams, OptionalTestProps {
  dataTestId?: string
  transformData: (items: T[]) => T[]
  selectedItems?: T[]
}

export function createGroupedActionRenderer<T>(
  ActionsButton: React.ComponentType<ActionsButtonProps<T>>,
  dataTransformerSelector: Selector<GlobalState, (items: T[]) => T[]> = createSelector(noop, () => (items: T[]) => items)
) {
  const mapStateToProps = createStructuredSelector({
    transformData: dataTransformerSelector
  })

  class GroupedActionRenderer extends React.Component<GroupedActionRendererProps<T> | ActionsRendererProps<T>> {
    render() {
      const { api, transformData, selectedItems, dataTestId } = this.props
      const items = selectedItems ?? api.getSelectedRows()
      const transformedItems = transformData(items)
      const shouldRenderActions = transformedItems.length > 0

      return shouldRenderActions ? (
        <HeaderCellContentWrapper>
          <ActionsButton items={transformedItems} isGroupRow={true} isCounterpartyNegotiation={false} dataTestId={dataTestId} />
        </HeaderCellContentWrapper>
      ) : null
    }

    refresh = () => {
      this.forceUpdate()
      return true
    }
  }

  return connect(mapStateToProps, null, null, { forwardRef: true })(withRenderOnEvents(GroupedActionRenderer, ['selectionChanged']))
}

export interface ActionsRendererProps<T = unknown> extends TypedCellRendererParams<T>, OptionalTestProps {
  transformData: (items: T[]) => T[]
  selectedItems?: T[]
}

export function createActionsRenderer<T>(
  ActionsButton: React.ComponentType<ActionsButtonProps<T>>,
  rowDataGetter?: RowDataGetter<any, any>,
  dataTransformerSelector: Selector<GlobalState, (items: T[]) => T[]> = createSelector(noop, () => (items: T[]) => items)
) {
  const mapStateToProps = createStructuredSelector({
    transformData: dataTransformerSelector
  })

  class ActionsRenderer extends React.Component<ActionsRendererProps<T>> {
    render() {
      const { api, node, context, transformData } = this.props

      const rowParams = this.props
      const rowData = rowDataGetter ? rowDataGetter(rowParams, true) : node.data
      const items = rowData ? [rowData] : getVisibleLeafChildren(api, node).map(n => n.data)
      const transformedItems = transformData(items).filter(item => !isEmpty(item))
      const selectedRows = api.getSelectedRows()
      const allChildSelected = intersection(items, selectedRows).length === items.length

      const shouldRenderActions = transformedItems.length > 0 && (!!rowData || allChildSelected)

      return shouldRenderActions ? (
        <DefaultCellContentWrapper>
          <ActionsButton
            gridApi={api}
            items={transformedItems}
            isGroupRow={!!node.group}
            context={context}
            isCounterpartyNegotiation={false}
          />
        </DefaultCellContentWrapper>
      ) : null
    }

    refresh = () => {
      this.forceUpdate()
      return true
    }
  }

  return connect(mapStateToProps, null, null, { forwardRef: true })(ActionsRenderer)
}
